import React, { useRef, useEffect, useContext } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import { RulerControl } from 'mapbox-gl-controls';
import { mapContext } from '../Services/MapContext';

import {
	DEFAULT_COORDS,
	DEFAULT_ZOOM,
	MAPBOX_TOKEN,
	LAYERS
} from '../Utilities/Config';

import 'mapbox-gl/dist/mapbox-gl.css';
import '../Styles/MapMapbox.css';

// left to right texts
mapboxgl.setRTLTextPlugin(
	'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
	null,
	true // Lazy load the plugin
);

const Map = (props) => {
	const {
		children,
		layersVisible,
		setLayersLoaded,
		onMouseMove,
		setTooltipContent,
		setHoverTooltipVisible
	} = props;

	mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_KEY || MAPBOX_TOKEN;

	const { map, setMap, mapLoaded, setMapLoaded, setRulerObject, rulerState } = useContext(mapContext);
	const mapContainer = useRef(null);
	const layersArray = useRef([]);
	const localRulerState = useRef(rulerState);
	const tooltipPrevId = useRef(null);
	const tooltipPrevProps = useRef(null);


	// show tooltip func
	const showTooltip = (layerName, content) => { setTooltipContent({id: layerName, content: content}); }
	
	// toggle layers on/off
	const toggleLayers = (layerIDs) => {
		if (map) {
			Object.entries(layerIDs).forEach((data) => {
				const layerId = data[0];
				const layerParams = data[1];
				const state = layerParams.state;

				changeLayerVisibility(layerId, state);

				// check if there is an additional outline layer
				let outlineLayerName = `${layerId}_outline`;
				if (map.getLayer(outlineLayerName)) {
					changeLayerVisibility(outlineLayerName, state);
				}
			});
		}
	}

	// update tooltip on mouse move
	const mouseLayerMove = (props, e, layerName) => {
		const tooltipSame = (tooltipPrevId.current === layerName);
		onMouseMove({left: e.point.x, top: e.point.y});
		if (!tooltipSame) {
			showTooltip(layerName, props);
		} else {
			showTooltip(layerName, tooltipPrevProps.current)
		}
			
		tooltipPrevId.current = layerName;
		tooltipPrevProps.current = props;
	}

	// change layer visibility
	// what is dis, huh?
	const changeLayerVisibility = (layerName, state) => {
		if (map.getLayer(layerName)) {
			map.setLayoutProperty(layerName, 'visibility', (state) ? 'visible' : 'none');
		}
		if (map.getLayer(layerName)) {
			map.setLayoutProperty(layerName, 'visibility', (state) ? 'visible' : 'none');
		}
		
		if (map.getLayer(`${layerName}-symbols-1`)) {
			map.setLayoutProperty(`${layerName}-symbols-1`, 'visibility', (state) ? 'visible' : 'none');
		}
		if (map.getLayer(`${layerName}-symbols-2`)) {
			map.setLayoutProperty(`${layerName}-symbols-2`, 'visibility', (state) ? 'visible' : 'none');
		}
	}

	// add additional polygonal layer
	const addPolygon = async (layerId) => {
		return await new Promise(async (resolve, reject) => {
			const layer = layersVisible[layerId];
			const layerName = layer.name;
			const sourceName = `${layerName}_src`;

			if (!map.getSource(sourceName)) {
				map.addSource(sourceName, {
					'type': 'geojson',
					'data': layer.data
				});
			} else {
				map.getSource(sourceName).setData(layer.data);
			}

			// pattern
			let paintProps = layer.paint ? layer.paint : {};
			if (layer.patternImageUrl && layer.patternImageName) {
				await new Promise((res, rej) => {
					map.loadImage(layer.patternImageUrl, (err, image) => {
						if (err) throw err;
						map.addImage(layer.patternImageName, image);
						res();
					});
				});
				paintProps = {
					...paintProps,
					'fill-pattern': layer.patternImageName
				};
			}

			// add an outline around the polygon
			if (layer.outlineColor) {
				map.addLayer({
					'id': `${layerId}_outline`,
					'type': 'line',
					'source': sourceName,
					'layout': {},
					'paint': {
						'line-opacity': layer.outlineOpacity,
						'line-color': layer.outlineColor,
						'line-width': layer.outlineWidth ? layer.outlineWidth : 3
					}
				});
				changeLayerVisibility(`${layerId}_outline`, layer.state);
			}

			// add a polygon
			if (!map.getLayer(layerName)) map.addLayer({
				'id': layerName,
				'type': layer.polyline ? 'line' : 'fill',
				'source': sourceName,
				'paint': paintProps
			});

			// labels next to polygons
			if (layer.labelField) {

				const layoutProps = {
					'icon-allow-overlap': true,
					'icon-ignore-placement': true,
					'text-allow-overlap': true,
					'symbol-placement': 'line',
					'text-font': ["Open Sans Regular"],
					'text-field': `{${layer.labelField}}`,
					'symbol-spacing': 100,
					'text-anchor': 'center',
					'text-size': 16
				};

				const paintProps = {
					"text-color": "#000000",
					"text-opacity": 0.1,
				}

				map.addLayer({
					'id': `${layerName}-symbols-1`,
					'type': 'symbol',
					'source': sourceName,
					'filter': ['==', ['get', layer.labelField], 'Deutschland'],
					'layout': {
						...layoutProps,
						'text-rotate': -4,
					},
					'paint': {
						...paintProps,
						"text-translate": [0, -60],
					}
				});
				map.addLayer({
					'id': `${layerName}-symbols-2`,
					'type': 'symbol',
					'source': sourceName,
					'filter': ['==', ['get', layer.labelField], 'Niederlande'],
					'layout': {
						...layoutProps,
						'text-rotate': 0,
					},
					'paint': {
						...paintProps,
						"text-translate": [-10, 60],
					}
				});

				changeLayerVisibility(`${layerName}-symbols-1`, layer.state);
				changeLayerVisibility(`${layerName}-symbols-2`, layer.state);
			}

			// interactivity
			if (layer.interactive === undefined || layer.interactive === true) {
				layersArray.current.push(layerName);
			}

			// reorder straight away if possible
			if (layer.beforeId && map.getLayer(layer.beforeId)) {
				map.moveLayer(layerName, layer.beforeId);
			}

			changeLayerVisibility(layerName, layer.state);
			resolve();
		})
	};

	// add point layer
	const addPointLayer = async (layerId, paramsBypass) => {
		return await new Promise(async (resolve, reject) => {
			const params = (paramsBypass) ? paramsBypass : LAYERS[layerId];
			const geojson = params.data;
	
			if (params.icon) {
				if (!map.hasImage(`custom-marker-${layerId}`)) {
					await new Promise((res, rej) => {
						map.loadImage(params.icon, (error, image) => {
							if (error) throw error;
							map.addImage(`custom-marker-${layerId}`, image);
							res();
						});
					});
				}
			}
	
			// Create source
			if (!map.getSource(layerId)) map.addSource(`${layerId}_src`, {
				'type': 'geojson',
				'data': geojson
			});
	
			// Create layers
			if (!map.getLayer(layerId)) map.addLayer({
				'id': layerId,
				'type': 'symbol',
				'source': `${layerId}_src`,
				'filter': ['==', ['geometry-type'], 'Point'],
				'layout': {
					'icon-image': `custom-marker-${layerId}`,
					'icon-size': 0.35,
					'icon-anchor': 'center',
					'icon-padding': 0,
					'icon-allow-overlap': true,
					'icon-ignore-placement': true,
					'text-allow-overlap': true,
					...params.layout
				},
				'paint': {
					'icon-opacity': 1,
				}
			});
	
			layersArray.current.push(layerId);

			// reorder straight away if possible
			if (params.beforeId && map.getLayer(params.beforeId)) {
				map.moveLayer(layerId, params.beforeId);
			}
	
			// set default layer visibility
			changeLayerVisibility(layerId, params.state);
			resolve();
		});
	}

	// add point circle layer
	const addCircleLayer = async (layerId) => {
		return await new Promise((resolve, reject) => {
			const params = LAYERS[layerId];
			const geojson = params.data;

			// Create source
			if (!map.getSource(layerId)) map.addSource(`${layerId}_src`, {
				'type': 'geojson',
				'data': geojson
			});

			// Create layers
			if (!map.getLayer(layerId)) map.addLayer({
				'id': layerId,
				'type': 'circle',
				'source': `${layerId}_src`,
				'paint': params.paint
			});

			// On marker click - show popup with data
			layersArray.current.push(layerId);

			// reorder straight away if possible
			if (params.beforeId && map.getLayer(params.beforeId)) {
				map.moveLayer(layerId, params.beforeId);
			}

			// set default layer visibility
			changeLayerVisibility(layerId, params.state);
			resolve();
		})
	}

	// reorder all the layers correctly
	const reorderLayers = () => {
		Object.entries(LAYERS).forEach(([key, layer]) => {
			if (!layer.beforeId && !map.getLayer(layer.beforeId)) return;

			// wait for the layer to load
			while (layer.beforeId && !map.getLayer(layer.beforeId)) {}
			map.moveLayer(key, layer.beforeId);

			// outlines
			if (layer.outlineColor) {
				map.moveLayer(`${key}_outline`, layer.beforeId);
			}

			// labels
			if (layer.labelField) {
				map.moveLayer(`${key}-symbols-1`, layer.beforeId);
				map.moveLayer(`${key}-symbols-2`, layer.beforeId);
			}
		});
	}

	// add geojson layers
	const addLayers = async (layers, params) => {

		const layerKeys = Object.keys(layers);
		await new Promise(async (resolve) => {
			for (let i = 0; i <= layerKeys.length-1; i++) {
				const layerId = layerKeys[i];
				const layerType = LAYERS[layerId].type;
				if (layerType === 'point') {
					await addPointLayer(layerId, params);
				} else if (layerType === 'circle') {
					await addCircleLayer(layerId);
				} else if (layerType === 'polygon') {
					await addPolygon(layerId);
				}

				if (i === layerKeys.length-1) resolve();
			}
		});

		setLayersLoaded(true);
		reorderLayers();
	}

	// Init map
	useEffect(() => {
		const initializeMap = ({ setMap, mapContainer }) => {
			const map = new mapboxgl.Map({
				container: mapContainer.current,
				style: 'mapbox://styles/greenpeacegmh/clkcm7nl3001w01qp4i0gbysb',
				center: DEFAULT_COORDS,
				zoom: DEFAULT_ZOOM,
				detectRetina: true,
				noWrap: true,
				// renderWorldCopies: false,
				continuousWorld: false,
				projection: 'mercator',
				// hash: true,
			});
			setMap(map);
		};

		if (!map) initializeMap({ setMap, mapContainer });
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setMap, map]);

	// Trigger on map load
	useEffect(() => {
		if (!map) return;
		map.on('load', () => { setMapLoaded(true); });

		// disable map rotation using right click + drag
		map.dragRotate.disable();
		// disable map rotation using touch rotation gesture
		map.touchZoomRotate.disableRotation();

		// Events to hide tooltip on screen drag
		map.on('touchmove', () => {
			setHoverTooltipVisible(false);
		});
		map.on('dragstart', () => {
			setHoverTooltipVisible(false);
		});

		// add controls
		map.addControl(new mapboxgl.ScaleControl(), 'bottom-right');
		const rulerControl = new RulerControl();
		setRulerObject(rulerControl.onAdd(map));
		
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map]);

	// Change marker depending on ruler state
	useEffect(() => {
		if (!map) return;
		map.getCanvas().style.cursor = (rulerState) ? 'cursor:crosshair !important' : 'cursor:default';
		localRulerState.current = rulerState;
	}, [rulerState, map]);

	// Load data and create layers
	useEffect(() => {
		if (!mapLoaded) return;
		addLayers(LAYERS);
		// Toggle layer visibility
		toggleLayers(layersVisible);

		const getFeaturesShowTooltip = (e) => {
			if (!e) {
				showTooltip(null, null);
				return;
			}

			if (!localRulerState.current) {
				const features = map.queryRenderedFeatures(e.point, { layers: layersArray.current });
				const feature = features[0];
				const featureId = feature.layer.id;
				let id = (featureId.includes('_outline')) ? featureId.split('_outline')[0] : featureId;
				const params = {...LAYERS[id], ...feature};
				mouseLayerMove(params, e, id);
			}
		}

		//onclick event
		map.on('click', layersArray.current, (e) => {
			if (!e.features || e.features.length === 0) return;
			getFeaturesShowTooltip(e);
		});

		// change mouse pointer and show a tooltip on mouseover
		map.on('mousemove', layersArray.current, (e) => {
			if (!e.features || e.features.length === 0) return;
			map.getCanvas().style.cursor = localRulerState.current ? 'crosshair' : 'pointer';
			getFeaturesShowTooltip(e);
		});
		map.on('mouseleave', layersArray.current, (e) => {
			map.getCanvas().style.cursor = localRulerState.current ? 'crosshair' : 'default';
			if (!localRulerState.current) showTooltip(null, null);
		});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mapLoaded]);

	// Adding event listener on change of layersEnabled
	useEffect(() => {
		toggleLayers(layersVisible);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [layersVisible]);

	// Render
	return (
		<div className="map-wrapper">
			{children}
			<div ref={mapContainer} className="map-container" />
		</div>
	);
};

export default Map;